import { dash } from "radash";
import { watchEffect } from "vue";

import { useJumplinks } from "./useJumplinks.ts";

import "./sJumplinks.css";

type JumpLinkStyle = "child" | "default" | "parent" | "strong";

type JumpLinkItem = {
  id: string;
  jumpLink: HTMLElement;
  target: HTMLElement;
};

const hasJumpLinks =
  typeof document !== "undefined" &&
  !!document.querySelector<HTMLElement>("[data-jumplink]");

function getJumpLinkMenus() {
  return document.querySelectorAll<HTMLElement>("[data-jumplink-menu]");
}

function jumpLinks(jumpLinksContainer: HTMLElement) {
  function jumpLinkTemplate({
    href,
    label,
    styling = [],
  }: {
    href: string;
    label: string;
    styling?: JumpLinkStyle[];
  }): HTMLElement {
    const template = document.createElement("template");
    const classes =
      styling.length > 0
        ? styling.map((style) => `s-jumplinks__jumplink--${style}`).join(" ")
        : "";
    template.innerHTML = `
      <li class="s-jumplinks__jumplink ${classes}">
        <a
          class="s-jumplinks__jumplink__link"
          href="${href}" data-gtm-type="link" data-gtm-location="sidebar-nav"
        >
          ${label}
        </a>
      </li>
    `;

    return template.content.querySelector<HTMLElement>(
      ".s-jumplinks__jumplink",
    )!;
  }

  function getJumplinkItems(): JumpLinkItem[] {
    const dataTargets =
      document.querySelectorAll<HTMLElement>("[data-jumplink]");

    return [...dataTargets].map((target) => {
      const { jumplink, jumplinkUrl, jumplinkStyle } = target.dataset;

      const label = jumplink || target.innerText;
      const idBase = jumplinkUrl || label;
      const id = encodeURIComponent(dash(idBase));
      const styling = jumplinkStyle
        ? (jumplinkStyle.split(/, ?/).filter(Boolean) as JumpLinkStyle[])
        : ([] as JumpLinkStyle[]);
      const href = `#${jumplinkUrl ?? id}`;

      if (!styling.includes("parent") && !styling.includes("child")) {
        styling.push("default");
      }

      return {
        id,
        target,
        jumpLink: jumpLinkTemplate({
          href,
          label,
          styling,
        }),
      };
    });
  }

  const jumpLinkItems = getJumplinkItems();

  function applyJumpLinkHtml() {
    if (jumpLinkItems.length === 0) {
      jumpLinksContainer.remove();
      document.querySelector(".s-jumplinks__title")?.remove();
      document.querySelector(".s-jumplinks__menu-title")?.remove();
      return;
    }

    jumpLinksContainer.innerHTML = "";
    jumpLinkItems.forEach((item) => {
      item.target.id = item.id;
      jumpLinksContainer.append(item.jumpLink);
    });
  }

  function initActiveJumpLink() {
    if (jumpLinkItems.length === 0) {
      return;
    }

    const { activeId } = useJumplinks(jumpLinkItems.map((item) => item.id));

    const jumpLinkById = new Map(
      jumpLinkItems.map((item) => [item.id, item.jumpLink]),
    );

    watchEffect(() => {
      const previous = document.querySelector<HTMLElement>(
        ".s-jumplinks__jumplink__link--active",
      );
      if (previous) {
        previous.classList.remove("s-jumplinks__jumplink__link--active");
      }

      const active = (
        activeId.value ? jumpLinkById.get(activeId.value) : undefined
      )?.querySelector<HTMLElement>(".s-jumplinks__jumplink__link");

      if (active) {
        active.classList.add("s-jumplinks__jumplink__link--active");

        const scrollContainer =
          document.querySelector<HTMLElement>(".s-jumplinks");
        if (scrollContainer?.contains(active)) {
          const activeRect = active.getBoundingClientRect();
          const containerRect = scrollContainer.getBoundingClientRect();

          const offset = activeRect.top - containerRect.top;
          const currentScroll = scrollContainer.scrollTop;

          scrollContainer.scrollTo({
            top:
              currentScroll +
              offset -
              containerRect.height / 2 +
              activeRect.height / 2,
            behavior: "smooth",
          });
        }
      }
    });
  }

  applyJumpLinkHtml();
  initActiveJumpLink();
}

function initJumplinkMenus(jumpLinksContainer: HTMLElement) {
  function initTitleInMenu(titleEl: HTMLElement, menuEl: HTMLElement) {
    const titleAttribute = menuEl.dataset.jumplinkMenuTitle;
    if (titleAttribute) {
      titleEl.innerText = titleAttribute;
    } else {
      titleEl.innerText =
        document.querySelector<HTMLElement>(".s-jumplinks__menu-title")
          ?.innerText || "";
    }
    menuEl.append(titleEl);
  }

  getJumpLinkMenus().forEach((menu) => {
    if (Object.hasOwn(menu.dataset, "jumplinkMenuStyle")) {
      const menuStyle = menu.dataset.jumplinkMenuStyle!;
      if (menuStyle === "style2") {
        const blockquote = document.createElement("blockquote");
        blockquote.classList.add("s-jumplink-menu--style-2");
        menu.parentNode?.insertBefore(blockquote, menu);
        blockquote.append(menu);
      }
      const titleEl = document.createElement("p");
      titleEl.classList.add("s-jumplink-menu--style-2__title");
      initTitleInMenu(titleEl, menu);
    } else {
      const titleEl = document.createElement("strong");
      initTitleInMenu(titleEl, menu);
    }

    menu.append(jumpLinksContainer.cloneNode(true));
    menu.classList.add("s-jumplink-menu-content");
  });
}

function initJumpLinks() {
  const jumpLinksContainer =
    document.querySelector<HTMLElement>(".s-jumplinks");
  if (!jumpLinksContainer) {
    return;
  }
  const layout = document.querySelector<HTMLElement>(".layout");
  if (layout) {
    layout.setAttribute(
      "style",
      "--row-sidebar-nav: 1fr; --row-sidebar-cta: 0",
    );
  }
  jumpLinks(jumpLinksContainer);
  if (jumpLinksContainer.children.length) {
    initJumplinkMenus(jumpLinksContainer);
  }
}

export { getJumpLinkMenus, hasJumpLinks, initJumpLinks };
